/**
 * Appointment Session Storage Utility
 * Centralized session storage management for appointment scheduling
 *
 * IMPORTANT: When modifying this file, update APPOINTMENT_SCHEDULING_FEATURE.md
 */

// Session storage keys
const SESSION_KEYS = {
    ZIP_CODE: 'validZipCode',
    ZIP_TIMESTAMP: 'zipCodeTimestamp',
    APPOINTMENT: 'selectedAppointment',
    APPOINTMENT_TIMESTAMP: 'appointmentTimestamp',
    DATE_OF_BIRTH: 'selectedDateOfBirth',
    DOB_TIMESTAMP: 'dobTimestamp'
};

// Session expiry time (1 hour in milliseconds)
const SESSION_EXPIRY = 60 * 60 * 1000;

/**
 * Generic session storage functions
 */

// Save data to session storage with timestamp
function saveToSession(key, data, timestampKey = null) {
    try {
        const dataToStore = typeof data === 'object' ? JSON.stringify(data) : data;
        sessionStorage.setItem(key, dataToStore);

        if (timestampKey) {
            sessionStorage.setItem(timestampKey, Date.now().toString());
        }


        return true;
    } catch (error) {
        return false;
    }
}

// Load data from session storage with expiry check
function loadFromSession(key, timestampKey = null, parseJson = false) {
    try {
        const data = sessionStorage.getItem(key);

        if (!data) {
            return null;
        }

        // Check expiry if timestamp key is provided
        if (timestampKey) {
            const timestamp = sessionStorage.getItem(timestampKey);
            if (timestamp) {
                const now = Date.now();
                const savedTime = parseInt(timestamp);

                if (now - savedTime > SESSION_EXPIRY) {
                    clearFromSession(key, timestampKey);
                    return null;
                }
            }
        }

        const result = parseJson ? JSON.parse(data) : data;

        return result;
    } catch (error) {
        return null;
    }
}

// Remove data from session storage
function clearFromSession(...keys) {
    try {
        keys.forEach(key => {
            sessionStorage.removeItem(key);
        });

        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Zip Code Session Storage Functions
 */

// Save zip code to session storage
function saveZipCodeToSession(zipCode) {
    const success = saveToSession(SESSION_KEYS.ZIP_CODE, zipCode, SESSION_KEYS.ZIP_TIMESTAMP);

    if (success) {
        // Sync with other zip inputs on the page
        syncAllZipInputs(zipCode);
    }

    return success;
}

// Load zip code from session storage
function loadZipCodeFromSession() {
    return loadFromSession(SESSION_KEYS.ZIP_CODE, SESSION_KEYS.ZIP_TIMESTAMP);
}

// Clear zip code from session storage
function clearZipCodeFromSession() {
    return clearFromSession(SESSION_KEYS.ZIP_CODE, SESSION_KEYS.ZIP_TIMESTAMP);
}

// Sync all zip inputs on the page with the given zip code
function syncAllZipInputs(zipCode) {
    try {
        // Update any visible simple zip inputs by id
        const primary = document.getElementById('appointment-zip-code-search');
        if (primary && primary.value !== zipCode) primary.value = zipCode;

        const secondary = document.getElementById('secondary-zip-code-search');
        if (secondary && secondary.value !== zipCode) secondary.value = zipCode;

        const hero = document.getElementById('llsa-zip-code-search');
        if (hero && hero.value !== zipCode) hero.value = zipCode;
    } catch (error) {
        // Silent error handling
    }
}

// Helper function to get zip input value
function getZipInputValue(container) {
    try {
        if (typeof getValue === 'function') {
            return getValue(container);
        }

        // Fallback: get from hidden input
        const hiddenInput = container.querySelector('.zip-code-input-hidden');
        return hiddenInput ? hiddenInput.value : '';
    } catch (error) {
        return '';
    }
}

/**
 * Appointment Session Storage Functions
 */

// Save selected appointment to session storage
function saveSelectedAppointmentToSession(appointmentData) {
    return saveToSession(SESSION_KEYS.APPOINTMENT, appointmentData, SESSION_KEYS.APPOINTMENT_TIMESTAMP);
}

// Load selected appointment from session storage
function loadSelectedAppointmentFromSession() {
    return loadFromSession(SESSION_KEYS.APPOINTMENT, SESSION_KEYS.APPOINTMENT_TIMESTAMP, true);
}

// Clear selected appointment from session storage
function clearSelectedAppointmentFromSession() {
    return clearFromSession(SESSION_KEYS.APPOINTMENT, SESSION_KEYS.APPOINTMENT_TIMESTAMP);
}

/**
 * Combined Operations
 */

// Clear appointment data when starting new zip code search
function clearAppointmentDataForNewSearch(searchType = '') {
    const success = clearSelectedAppointmentFromSession();

    if (success) {
        // Also clear global appointment data if it exists
        if (typeof selectedAppointmentData !== 'undefined') {
            selectedAppointmentData = null;
        }
    }

    return success;
}

// Save date of birth to session storage
function saveDateOfBirthToSession(dateOfBirth) {
    return saveToSession(SESSION_KEYS.DATE_OF_BIRTH, dateOfBirth, SESSION_KEYS.DOB_TIMESTAMP);
}

// Load date of birth from session storage
function loadDateOfBirthFromSession() {
    return loadFromSession(SESSION_KEYS.DATE_OF_BIRTH, SESSION_KEYS.DOB_TIMESTAMP);
}

// Clear date of birth from session storage
function clearDateOfBirthFromSession() {
    return clearFromSession(SESSION_KEYS.DATE_OF_BIRTH, SESSION_KEYS.DOB_TIMESTAMP);
}

// Clear all session data
function clearAllSessionData() {
    return clearFromSession(
        SESSION_KEYS.ZIP_CODE,
        SESSION_KEYS.ZIP_TIMESTAMP,
        SESSION_KEYS.APPOINTMENT,
        SESSION_KEYS.APPOINTMENT_TIMESTAMP,
        SESSION_KEYS.DATE_OF_BIRTH,
        SESSION_KEYS.DOB_TIMESTAMP
    );
}

// Check if session has valid data
function hasValidZipCodeInSession() {
    const zipCode = loadZipCodeFromSession();
    return zipCode && isValidZipFormat(zipCode);
}

function hasValidAppointmentInSession() {
    const appointment = loadSelectedAppointmentFromSession();
    return appointment && appointment._appointment_id;
}

/**
 * Export functions for global access
 */
window.AppointmentSessionStorage = {
    // Generic functions
    saveToSession,
    loadFromSession,
    clearFromSession,

    // Zip code functions
    saveZipCodeToSession,
    loadZipCodeFromSession,
    clearZipCodeFromSession,
    syncAllZipInputs,

    // Appointment functions
    saveSelectedAppointmentToSession,
    loadSelectedAppointmentFromSession,
    clearSelectedAppointmentFromSession,

    // Date of birth functions
    saveDateOfBirthToSession,
    loadDateOfBirthFromSession,
    clearDateOfBirthFromSession,

    // Combined operations
    clearAppointmentDataForNewSearch,
    clearAllSessionData,
    hasValidZipCodeInSession,
    hasValidAppointmentInSession,

    // Constants
    SESSION_KEYS,
    SESSION_EXPIRY
};

// Backward compatibility - expose individual functions globally
window.saveZipCodeToSession = saveZipCodeToSession;
window.loadZipCodeFromSession = loadZipCodeFromSession;
window.clearZipCodeFromSession = clearZipCodeFromSession;
window.saveSelectedAppointmentToSession = saveSelectedAppointmentToSession;
window.loadSelectedAppointmentFromSession = loadSelectedAppointmentFromSession;
window.clearSelectedAppointmentFromSession = clearSelectedAppointmentFromSession;
window.saveDateOfBirthToSession = saveDateOfBirthToSession;
window.loadDateOfBirthFromSession = loadDateOfBirthFromSession;
window.clearDateOfBirthFromSession = clearDateOfBirthFromSession;
window.clearAppointmentDataForNewSearch = clearAppointmentDataForNewSearch;
